/**
 * Ajustes — os quatro catálogos do §5.16 da spec 04.
 *
 * ## Por que esta tela importa mais do que parece
 *
 * O §5.16 diz: *"são quatro telas banais que a spec 05 não funciona sem — e é justamente por serem
 * banais que ficam de fora até alguém tentar finalizar um atendimento e descobrir que não há motivo
 * para escolher"*.
 *
 * O H31 construiu a escrita e ela **nunca teve face**: criar um motivo era `curl`. Esta tela é essa
 * face.
 *
 * ## O que a spec fixa, item por item
 *
 * - **Motivo pode ter mensagem de encerramento**, e a tela avisa **ali mesmo** que ela passa pelo
 *   `podeEnviar()` — se o atendimento durou mais que a janela, a despedida não sai.
 * - **Exclusão é lógica.** O botão diz `Arquivar`, não `Excluir`: `atendimentos.motivo_id` é FK, e
 *   apagar corromperia relatório histórico.
 * - **Etiqueta é da conversa, não do atendimento** — "cliente VIP" não deixa de ser verdade quando o
 *   atendimento fecha.
 * - Sem `manage_catalog`, os controles ficam **desabilitados com o motivo**, não escondidos (§2).
 */

import { useState, type ReactNode } from "react";
import { useAbaDaUrl } from "./acoes-do-dono.js";
import { EstadoDaFalha, falhaDe, useSessao, Etiqueta } from "@hugi/ui";
import { CabecalhoDaTela, Carregando, Dica, GradeDeCartoes, PaginaInteira, Selo } from "@hugi/ui/kit";
import { Settings } from "lucide-react";
import { useCatalogos, useMutacaoDeCatalogo } from "./dados.js";
import { AbaDeAjustes, Acao, Gerais } from "./Gerais.js";
import { Plano } from "./Plano.js";
import "./ajustes.css";

type Aba = "gerais" | "plano" | "motivos" | "filas" | "etiquetas" | "respostas";

const ABAS: readonly { id: Aba; rotulo: string }[] = [
  // `gerais` PRIMEIRO: e' a aba que decide se o produto manda mensagem sozinho, e a que um cliente novo
  // precisa ver antes de qualquer catalogo.
  { id: "gerais", rotulo: "Gerais" },
  // `plano` logo depois de `gerais`: e' a pergunta que o dono da conta faz todo mes, e ate' aqui
  // ela nao tinha resposta em tela nenhuma.
  { id: "plano", rotulo: "Plano e consumo" },
  { id: "motivos", rotulo: "Motivos de encerramento" },
  { id: "filas", rotulo: "Filas" },
  { id: "etiquetas", rotulo: "Etiquetas" },
  { id: "respostas", rotulo: "Respostas rápidas" },
];

/**
 * A TELA (spec 22): Cadastro em ABAS. As seis seções são abas de um cartão só; cada aba rola por
 * dentro, e o que salva (o Salvar de Gerais, o Criar de cada catálogo) fica fora do que rola — o
 * defeito histórico daqui era o Salvar no fim de oito cartões, fora de alcance.
 */
export function Ajustes() {
  // A aba mora na URL (`/ajustes?aba=plano`, spec 23): o Início manda direto ao plano, e o link sobrevive ao recarregar.
  const [aba, setAba] = useAbaDaUrl<Aba>(ABAS.map((a) => a.id), "gerais");
  const catalogos = useCatalogos();

  let corpo: ReactNode;
  if (catalogos.error) {
    corpo = (
      <div className="v2-cartao hugi-ajustes-estado">
        <EstadoDaFalha
          falha={falhaDe(catalogos.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
          aoRepetir={() => void catalogos.refetch()}
        />
      </div>
    );
  } else if (catalogos.isPending) {
    corpo = (
      <div className="v2-cartao hugi-ajustes-estado">
        <Carregando>Carregando os ajustes…</Carregando>
      </div>
    );
  } else {
    corpo = (
      <GradeDeCartoes
        rotulo="Ajustes"
        cartoes={[
          {
            id: "ajustes",
            titulo: "Catálogos",
            aba,
            aoTrocarAba: (id) => setAba(id as Aba),
            abas: ABAS.map((a) => ({ id: a.id, rotulo: a.rotulo, conteudo: () => <Secao aba={a.id} /> })),
          },
        ]}
        colunas="minmax(0, 1fr)"
      />
    );
  }

  return (
    <PaginaInteira rotulo="Ajustes" classe="hugi-ajustes">
      <CabecalhoDaTela
        icone={<Settings size={16} />}
        titulo="Ajustes"
        dica="O que a rotina de atendimento usa: horário, mensagens automáticas, filas, motivos e etiquetas."
      />
      {corpo}
    </PaginaInteira>
  );
}

function Secao(props: { readonly aba: Aba }) {
  switch (props.aba) {
    case "gerais":
      return <Gerais />;
    case "plano":
      return (
        <AbaDeAjustes>
          <Plano />
        </AbaDeAjustes>
      );
    case "motivos":
      return <Motivos />;
    case "filas":
      return <Filas />;
    case "etiquetas":
      return <Etiquetas />;
    case "respostas":
      return <Respostas />;
  }
}

/** A moldura de um catálogo: explicação, formulário e lista. */
function Catalogo(props: {
  /** A explicação do catálogo — vai no (i), não em parágrafo. */
  readonly explicacao: string;
  /** "3 motivos". */
  readonly contagem: string;
  readonly formulario: ReactNode;
  /** O rótulo do botão que abre o formulário — "Criar fila". O formulário só aparece depois do clique. */
  readonly rotuloDeCriar: string;
  readonly children: ReactNode;
  readonly aviso?: string | null;
}) {
  const [criando, setCriando] = useState(false);
  return (
    <AbaDeAjustes
      barra={
        <>
          <span className="hugi-ajustes-contagem">{props.contagem}</span>
          <Dica texto={props.explicacao} />
          <span className="hugi-ajustes-barra-acoes">
            <Acao tom={criando ? "secundaria" : "primaria"} pequeno onClick={() => setCriando((c) => !c)}>
              {criando ? "Fechar" : props.rotuloDeCriar}
            </Acao>
          </span>
        </>
      }
    >
      {criando ? <div className="hugi-ajustes-bloco">{props.formulario}</div> : null}
      {props.aviso ? (
        <p role="alert" className="hugi-ajustes-atencao">
          {props.aviso}
        </p>
      ) : null}
      <ul className="hugi-ajustes-lista">{props.children}</ul>
    </AbaDeAjustes>
  );
}

function Linha(props: { readonly children: ReactNode; readonly acoes?: ReactNode }) {
  return (
    <li className="hugi-ajustes-item">
      <div className="hugi-ajustes-item-corpo">{props.children}</div>
      {props.acoes}
    </li>
  );
}

/** O campo de texto, com o vidro de controle. */
function Campo(props: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (v: string) => void;
  readonly exemplo?: string;
  readonly tipo?: "texto" | "cor";
}) {
  return (
    <label htmlFor={props.id} className="hugi-ajustes-campo">
      <span>{props.rotulo}</span>
      <input
        id={props.id}
        type={props.tipo === "cor" ? "color" : "text"}
        value={props.valor}
        onChange={(e) => props.aoMudar(e.target.value)}
        placeholder={props.exemplo}
        // o seletor de cor tem geometria propria (36px de altura, quadrado).
        {...(props.tipo === "cor" ? { className: "hugi-ajustes-cor" } : {})}
      />
    </label>
  );
}

/** `true` quando quem está olhando pode gerenciar catálogo. */
function usePodeGerenciar(): { pode: boolean; motivo: string } {
  const sessao = useSessao();
  const pode = sessao.permissoes.includes("manage_catalog");
  return {
    pode,
    motivo: "Gerenciar catálogo exige a permissão manage_catalog — um administrador concede.",
  };
}

function Motivos() {
  const catalogos = useCatalogos();
  const mutar = useMutacaoDeCatalogo<{ id: string }>();
  const { pode, motivo: motivoDaPermissao } = usePodeGerenciar();
  const [nome, setNome] = useState("");
  const [despedida, setDespedida] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);

  const criar = async () => {
    setAviso(null);
    const r = await mutar.mutateAsync({
      caminho: "/motivos",
      corpo: { nome, ...(despedida.trim() ? { mensagemDeEncerramento: despedida } : {}) },
    });
    if (r.ok) {
      setNome("");
      setDespedida("");
    } else {
      // A frase vem do SERVIDOR — "Já existe um motivo chamado X". Reescrevê-la no front criaria duas
      // verdades sobre a mesma regra, e a do front envelhece primeiro (§6 da spec 02).
      setAviso(r.falha?.erro.mensagem ?? "Não foi possível criar.");
    }
  };

  const arquivar = async (id: string) => {
    setAviso(null);
    const r = await mutar.mutateAsync({ caminho: `/motivos/${id}`, metodo: "DELETE" });
    if (!r.ok) setAviso(r.falha?.erro.mensagem ?? "Não foi possível arquivar.");
  };

  const lista = catalogos.data?.motivos ?? [];

  return (
    <Catalogo
      rotuloDeCriar="Criar motivo"
      aviso={aviso}
      contagem={lista.length === 1 ? "1 motivo" : `${lista.length} motivos`}
      explicacao="A lista fechada que o encerramento exige. Sem nenhum motivo, ninguém finaliza atendimento — por isso o Hugi cria três no primeiro acesso, e deixa você apagar todos."
      formulario={
        <div className="hugi-ajustes-form">
          <div className="hugi-ajustes-form-linha">
            <Campo id="motivo-nome" rotulo="Nome" valor={nome} aoMudar={setNome} exemplo="Ex.: Orçamento enviado" />
          </div>
          <Campo
            id="motivo-despedida"
            rotulo="Mensagem de encerramento (opcional)"
            valor={despedida}
            aoMudar={setDespedida}
            exemplo="Ex.: Enviamos seu orçamento! Qualquer dúvida, chame."
          />
          {/*
            O aviso do §5.16b, DITO no formulário e não depois: a despedida passa pelo `podeEnviar()`, e
            se o atendimento durou mais que a janela ela não sai. Descobrir isso ao encerrar é pior.
          */}
          {despedida.trim() ? (
            <p className="hugi-ajustes-atencao">
              Esta mensagem passa pela mesma checagem de qualquer envio: se o atendimento durou mais que
              a janela do canal, ela não sai — e a tela dirá isso em vez de fingir que saiu.
            </p>
          ) : null}
          <div>
            <Acao
              tom="primaria"
              onClick={() => void criar()}
              desabilitado={!pode || !nome.trim() || mutar.isPending}
              motivo={!pode ? motivoDaPermissao : "Dê um nome ao motivo."}
            >
              Criar motivo
            </Acao>
          </div>
        </div>
      }
    >
      {lista.map((m) => (
        <Linha
          key={m.id}
          acoes={
            <Acao
              onClick={() => void arquivar(m.id)}
              desabilitado={!pode || m.automatico || mutar.isPending}
              motivo={
                !pode
                  ? motivoDaPermissao
                  : "Motivo automático é usado pelo sistema no encerramento por inatividade."
              }
            >
              Arquivar
            </Acao>
          }
        >
          <div className="hugi-ajustes-item-titulo">
            <span className="hugi-ajustes-forte">{m.nome}</span>
            {/* `automatico` é o que o sistema usa sozinho. Nunca se confunde com decisão de gente em
                relatório — e é por isso que ele aparece marcado. */}
            {m.automatico ? <Selo tom="neutro">Automático</Selo> : null}
          </div>
          {m.mensagemDeEncerramento ? (
            <p className="hugi-ajustes-item-sub">
              Despedida: “{m.mensagemDeEncerramento}”
            </p>
          ) : null}
        </Linha>
      ))}
    </Catalogo>
  );
}

function Filas() {
  const catalogos = useCatalogos();
  const mutar = useMutacaoDeCatalogo<{ id: string }>();
  const { pode, motivo } = usePodeGerenciar();
  const [nome, setNome] = useState("");
  const [distribuicao, setDistribuicao] = useState("manual");
  const [aviso, setAviso] = useState<string | null>(null);

  const criar = async () => {
    setAviso(null);
    const r = await mutar.mutateAsync({ caminho: "/filas", corpo: { nome, distribuicao } });
    if (r.ok) setNome("");
    else setAviso(r.falha?.erro.mensagem ?? "Não foi possível criar.");
  };

  const arquivar = async (id: string) => {
    setAviso(null);
    const r = await mutar.mutateAsync({ caminho: `/filas/${id}`, metodo: "DELETE" });
    // O 409 daqui traz **quantos** atendimentos estão abertos na fila — a frase do servidor é acionável,
    // e reescrevê-la perderia o número.
    if (!r.ok) setAviso(r.falha?.erro.mensagem ?? "Não foi possível arquivar.");
  };

  const lista = catalogos.data?.filas ?? [];

  return (
    <Catalogo
      rotuloDeCriar="Criar fila"
      aviso={aviso}
      contagem={lista.length === 1 ? "1 fila" : `${lista.length} filas`}
      explicacao="Filas são os departamentos. A fila de uma conexão decide onde a conversa cai quando chega — e um atendente só vê a fila de que participa, a menos que tenha permissão de ver o trabalho da equipe."
      formulario={
        <div className="hugi-ajustes-form-linha">
          <Campo id="fila-nome" rotulo="Nome" valor={nome} aoMudar={setNome} exemplo="Ex.: Recepção" />
          <label htmlFor="fila-dist" className="hugi-ajustes-campo">
            <span>Distribuição</span>
            <select id="fila-dist" value={distribuicao} onChange={(e) => setDistribuicao(e.target.value)}>
              <option value="manual">Manual — o atendente pega da fila</option>
              <option value="rodizio">Rodízio</option>
              <option value="menor_fila">Quem tem menos atendimentos</option>
            </select>
          </label>
          <Acao
            tom="primaria"
            onClick={() => void criar()}
            desabilitado={!pode || !nome.trim() || mutar.isPending}
            motivo={!pode ? motivo : "Dê um nome à fila."}
          >
            Criar fila
          </Acao>
        </div>
      }
    >
      {lista.map((f) => (
        <Linha
          key={f.id}
          acoes={
            <Acao
              onClick={() => void arquivar(f.id)}
              desabilitado={!pode || mutar.isPending}
              motivo={motivo}
            >
              Arquivar
            </Acao>
          }
        >
          <div className="hugi-ajustes-item-titulo">
            <span className="hugi-ajustes-forte">{f.nome}</span>
            {f.minhas ? <Selo tom="sucesso">Você atende</Selo> : null}
            {/*
              O selo "Acesso restrito" saiu em 2026-08-17 (H70, ADR-0013). Ele lia
              `fila_atendentes.restrito`, uma coluna que **nada liga e nada consulta para agir** —
              nenhuma tela a define, e nenhum `WHERE` a lê. Um selo é uma afirmação sobre o estado
              do sistema, e este afirmava um recorte de acesso que não existia.

              Quem restringe é o interruptor "Ver só as filas dela", na tela de Equipe, que é por
              PESSOA e passou a valer de verdade. Duas regras para a mesma ideia é como uma delas
              fica errada — a ADR escolhe a que tem tela e significado definido.
            */}
          </div>
        </Linha>
      ))}
    </Catalogo>
  );
}

function Etiquetas() {
  const catalogos = useCatalogos();
  const mutar = useMutacaoDeCatalogo<{ id: string }>();
  const { pode, motivo } = usePodeGerenciar();
  const [nome, setNome] = useState("");
  /**
   * A cor inicial do seletor.
   *
   * Hexadecimal e' **obrigatorio** aqui: `<input type="color">` so aceita `#rrggbb`, e nao resolve
   * `var()`. E' a excecao que a catraca da palheta tolera na baseline — e o valor e' o `--azul`, que a
   * tabela do `vidro.css` documenta como o azul da Meta 20% mais claro.
   */
  const [cor, setCor] = useState("#3383E6");
  const [aviso, setAviso] = useState<string | null>(null);

  const criar = async () => {
    setAviso(null);
    const r = await mutar.mutateAsync({ caminho: "/etiquetas", corpo: { nome, cor } });
    if (r.ok) setNome("");
    else setAviso(r.falha?.erro.mensagem ?? "Não foi possível criar.");
  };

  const lista = catalogos.data?.etiquetas ?? [];

  return (
    <Catalogo
      rotuloDeCriar="Criar etiqueta"
      aviso={aviso}
      contagem={lista.length === 1 ? "1 etiqueta" : `${lista.length} etiquetas`}
      explicacao="Etiqueta é do contato, não do atendimento: “cliente VIP” não deixa de ser verdade quando o atendimento fecha. Aparece como chip na lista de conversas e no fio."
      formulario={
        <div className="hugi-ajustes-form-linha">
          <Campo id="et-nome" rotulo="Nome" valor={nome} aoMudar={setNome} exemplo="Ex.: Convênio" />
          <Campo id="et-cor" rotulo="Cor" valor={cor} aoMudar={setCor} tipo="cor" />
          <Acao
            tom="primaria"
            onClick={() => void criar()}
            desabilitado={!pode || !nome.trim() || mutar.isPending}
            motivo={!pode ? motivo : "Dê um nome à etiqueta."}
          >
            Criar etiqueta
          </Acao>
        </div>
      }
    >
      {lista.map((e) => (
        <Linha
          key={e.id}
          acoes={
            <Acao
              onClick={() =>
                void mutar
                  .mutateAsync({ caminho: `/etiquetas/${e.id}`, metodo: "DELETE" })
                  .then((r) => !r.ok && setAviso(r.falha?.erro.mensagem ?? "Não foi possível arquivar."))
              }
              desabilitado={!pode || mutar.isPending}
              motivo={motivo}
            >
              Arquivar
            </Acao>
          }
        >
          <Etiqueta nome={e.nome} cor={e.cor} className="px-2 py-0.5 text-xs" />
        </Linha>
      ))}
    </Catalogo>
  );
}

function Respostas() {
  const catalogos = useCatalogos();
  const mutar = useMutacaoDeCatalogo<{ id: string }>();
  const sessao = useSessao();
  const [atalho, setAtalho] = useState("");
  const [texto, setTexto] = useState("");
  const [visibilidade, setVisibilidade] = useState("tenant");
  const [aviso, setAviso] = useState<string | null>(null);

  /**
   * Resposta rápida exige `respond_conversation`, e **não** `manage_catalog`.
   *
   * Porque atalho pessoal é organização do próprio trabalho: exigir permissão de administração para
   * criar o próprio `/orcamento` faria o atendente pedir ajuda para arrumar a mesa dele.
   */
  const pode = sessao.permissoes.includes("respond_conversation");

  const criar = async () => {
    setAviso(null);
    const r = await mutar.mutateAsync({
      caminho: "/respostas-rapidas",
      corpo: { atalho, texto, visibilidade },
    });
    if (r.ok) {
      setAtalho("");
      setTexto("");
    } else {
      setAviso(r.falha?.erro.mensagem ?? "Não foi possível criar.");
    }
  };

  const lista = catalogos.data?.respostasRapidas ?? [];

  return (
    <Catalogo
      rotuloDeCriar="Criar resposta"
      aviso={aviso}
      contagem={lista.length === 1 ? "1 resposta" : `${lista.length} respostas`}
      explicacao="Digitadas como /atalho no compositor. O escopo decide quem vê: pessoal é só sua, fila é de quem atende aquela fila, e tenant é de todos."
      formulario={
        <div className="hugi-ajustes-form">
          <div className="hugi-ajustes-form-linha">
            <Campo id="rr-atalho" rotulo="Atalho" valor={atalho} aoMudar={setAtalho} exemplo="/orcamento" />
            {/* `min-w-0`: sem ele o rótulo não encolhe (min-width de item de flex é `auto`), o
                seletor fica com 265px num espaço de 203 e a PÁGINA passa a rolar na horizontal —
                medido a 375px, `main` 306/285. */}
            <label htmlFor="rr-vis" className="hugi-ajustes-campo">
              <span>Quem vê</span>
              <select id="rr-vis" value={visibilidade} onChange={(e) => setVisibilidade(e.target.value)}>
                <option value="tenant">Todos</option>
                <option value="pessoal">Só eu</option>
              </select>
            </label>
          </div>
          <Campo id="rr-texto" rotulo="Texto" valor={texto} aoMudar={setTexto} exemplo="O texto que será inserido" />
          <div>
            <Acao
              tom="primaria"
              onClick={() => void criar()}
              desabilitado={!pode || !atalho.trim() || !texto.trim() || mutar.isPending}
              motivo={
                !pode ? "Criar resposta rápida exige a permissão respond_conversation." : "Preencha atalho e texto."
              }
            >
              Criar resposta
            </Acao>
          </div>
          {/*
            O escopo `fila` não está no seletor: ele exige escolher a fila, e a validação disso já existe
            no servidor. Pôr a opção sem o seletor de fila daria um 422 previsível — pior que a ausência.
          */}
          <p className="hugi-ajustes-mudo">O escopo por fila existe na API e ainda não tem seletor aqui.</p>
        </div>
      }
    >
      {lista.map((r) => (
        <Linha
          key={r.id}
          acoes={
            <Acao
              onClick={() =>
                void mutar
                  .mutateAsync({ caminho: `/respostas-rapidas/${r.id}`, metodo: "DELETE" })
                  .then((x) => !x.ok && setAviso(x.falha?.erro.mensagem ?? "Não foi possível arquivar."))
              }
              desabilitado={!pode || mutar.isPending}
              motivo="Só quem criou pode arquivar um atalho pessoal."
            >
              Arquivar
            </Acao>
          }
        >
          <div className="hugi-ajustes-item-titulo">
            <code className="hugi-ajustes-forte">{r.atalho}</code>
            <Selo tom="neutro">
              {r.visibilidade === "pessoal" ? "Só você" : r.visibilidade === "fila" ? "Da fila" : "Todos"}
            </Selo>
          </div>
          <p className="hugi-ajustes-item-sub">{r.texto}</p>
        </Linha>
      ))}
    </Catalogo>
  );
}
